<template>
  <multiselect
    v-model="internal"
    :track-by="trackBy"
    :label="label"
    :options="options"
    :searchable="searchable"
    :placeholder="placeholder"
    :multiple="multiple"
    :hide-selected="multiple ? true : false"
    :allow-empty="allowEmpty"
    :group-values="groupValues"
    :group-label="groupLabel"
    :group-select="groupSelect"
    :show-labels="false"
    :class="classes"
    :disabled="disabled"
    :close-on-select="closeOnSelect"
    :max-height="maxHeight"
    @input="emitEvent"
  />
</template>

<script>
import Multiselect from "vue-multiselect";
import clone from "lodash/clone";
import intersectionBy from "lodash/intersectionBy";

const _ = { clone, intersectionBy };

export default {
  name: "BaseSelect",
  components: {
    Multiselect
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    },
    value: {
      required: true
    },
    options: {
      type: Array,
      required: true
    },
    trackBy: {
      type: String,
      default: "id"
    },
    label: {
      type: String,
      default: "name"
    },
    getObject: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: "Wybierz..."
    },
    multiple: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
    groupValues: {
      type: String,
      default: "",
      required: false
    },
    groupLabel: {
      type: String,
      default: "",
      required: false
    },
    groupSelect: {
      type: Boolean,
      default: false,
      required: false
    },
    error: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      type: Number,
      default: 300
    },
    blockTag: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internal: []
    };
  },
  computed: {
    hasError() {
      return !!this.error;
    },
    classes() {
      return {
        "multiselect--block-tag": this.blockTag,
        "is-invalid": this.hasError
      };
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        // if (newValue != oldValue) {
        this.setValues(newValue);
        // }
      }
    },
    options() {
      this.setValues(this.value);
    }
  },
  created() {
    this.setValues(this.value);
  },
  methods: {
    setValues(values) {
      // let selected = [];

      if (Object.prototype.toString.call(values) !== "[object Array]") {
        values = [values];
      }

      if (typeof values[0] === "string" || typeof values[0] === "number") {
        values = values.map(v => {
          const elem = {};
          elem[this.trackBy] = v;
          return elem;
        });
      }

      this.internal = _.intersectionBy(this.options, values, this.trackBy);
    },
    emitEvent(event) {
      let values = null;

      if (this.getObject) {
        values = this.internal;
      } else if (this.multiple) {
        values = this.internal.map(v => v[this.trackBy]);
      } else {
        values =
          this.internal && this.internal[this.trackBy]
            ? this.internal[this.trackBy]
            : null;
      }

      this.$emit("input", values);
    }
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
@import "@/styles/_variables.scss";

.multiselect {
  min-height: 38px;
}

.multiselect__tags {
  padding-top: 9px;
  min-height: 38px;
  border: 1px solid #ced4da;
  border-radius: 0;
  font-size: 1rem;
  padding-left: 0.75rem;
}

.multiselect.is-invalid .multiselect__tags {
  border-color: $col-danger;
}

.multiselect__content-wrapper {
  border-radius: 0;
  background: lighten($col-gray, 27%);
}

.multiselect--above .multiselect__content-wrapper {
  border-radius: 0;
}

.multiselect__option {
  font-size: 0.85em;
  min-height: 30px;
  padding: 6px 1.1rem;
}

.multiselect__option--highlight {
  background: $col-secondary;
}
.multiselect__option--selected.multiselect__option--highlight {
  background: $col-primary;
}

.multiselect__single {
  margin-bottom: 0;
}

.multiselect__placeholder {
  position: relative;
  display: inline-block;
  min-height: 20px;
  line-height: 20px;
  border: none;
  border-radius: 5px;
  background: #fff;
  padding: 0 0 0 5px;
  width: 100%;
  vertical-align: top;
  margin-bottom: 0;
}

.multiselect__tag {
  position: relative;
  display: inline-block;
  padding: 0 14px 0 0;
  border-radius: 0;
  margin-right: 15px;
  color: $col-text;
  margin-top: 2px;
  background: white;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
}

.multiselect--block-tag .multiselect__tag {
  display: block;
  padding-bottom: 8px;
}

.multiselect__tag-icon {
  margin-left: 0;
  text-align: left;
  line-height: 17px;
  opacity: 0.5;
  width: 10px;
  &:hover {
    background: white;
    opacity: 1;
  }
}

.multiselect__tag-icon:after {
  content: "\D7";
  color: $col-text;
  font-size: 20px;
}

.multiselect__tag-icon:focus:after,
.multiselect__tag-icon:hover:after {
  color: $col-text;
}

.multiselect:focus {
  color: $col-text;
  background-color: #fff;
  border-color: #84d2f1;
  outline: 0;
  -webkit-box-shadow: 0 0 0 0.2rem rgba(25, 164, 220, 0.25);
  box-shadow: 0 0 0 0.2rem rgba(25, 164, 220, 0.25);
}

.multiselect--disabled {
  opacity: 1;
  .multiselect__tags {
    border-color: $col-gray;
    background: lighten($col-gray, 5%);
  }
  .multiselect__select {
    display: none;
  }
  .multiselect__single,
  .multiselect__tag {
    color: black;
    background: lighten($col-gray, 5%);
  }
}
</style>
